import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStore } from "../store";

export default function ProjectView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentProject = useStore((s) => s.currentProject);
  const environments = useStore((s) => s.environments);
  const trainingJobs = useStore((s) => s.trainingJobs);
  const deployments = useStore((s) => s.deployments);
  const loading = useStore((s) => s.loading);
  const fetchProject = useStore((s) => s.fetchProject);
  const fetchEnvironments = useStore((s) => s.fetchEnvironments);
  const fetchTrainingJobs = useStore((s) => s.fetchTrainingJobs);
  const fetchDeployments = useStore((s) => s.fetchDeployments);

  useEffect(() => {
    if (id) {
      fetchProject(id);
      fetchEnvironments(id);
      fetchTrainingJobs(id);
      fetchDeployments(id);
    }
  }, [id, fetchProject, fetchEnvironments, fetchTrainingJobs, fetchDeployments]);

  if (loading && !currentProject) {
    return <p className="text-muted">Loading project...</p>;
  }

  if (!currentProject) {
    return <p className="text-muted">Project not found.</p>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{currentProject.name}</h1>
          <p className="page-subtitle">{currentProject.description}</p>
        </div>
        <span
          className={`badge ${
            currentProject.status === "active"
              ? "badge-success"
              : "badge-neutral"
          }`}
        >
          {currentProject.status}
        </span>
      </div>

      {/* Quick stats */}
      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-value">{environments.length}</div>
          <div className="stat-label">Environments</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {trainingJobs.filter((j) => j.status === "running").length}
          </div>
          <div className="stat-label">Active Training</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{deployments.length}</div>
          <div className="stat-label">Deployments</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          Quick Actions
        </h2>
        <div className="flex gap-2">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/projects/${id}/scan`)}
          >
            Upload Scan
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/projects/${id}/training`)}
          >
            Manage Training
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/projects/${id}/deploy`)}
          >
            Deployments
          </button>
        </div>
      </div>

      {/* Recent environments */}
      {environments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Environments
          </h2>
          <div className="card-grid">
            {environments.map((env) => (
              <div
                key={env.id}
                className="card"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(`/projects/${id}/environment/${env.id}`)
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    navigate(`/projects/${id}/environment/${env.id}`);
                }}
              >
                <div className="card-header">
                  <div className="card-title">{env.name}</div>
                  <span
                    className={`badge ${
                      env.status === "ready"
                        ? "badge-success"
                        : env.status === "processing"
                          ? "badge-warning"
                          : env.status === "failed"
                            ? "badge-danger"
                            : "badge-info"
                    }`}
                  >
                    {env.status}
                  </span>
                </div>
                <div className="card-meta">
                  {env.source_type === "video" ? "Video scan" : "Photo set"} -{" "}
                  {new Date(env.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent training jobs */}
      {trainingJobs.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Training Jobs
          </h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Model</th>
                  <th>Scenario</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {trainingJobs.slice(0, 5).map((job) => (
                  <tr key={job.id}>
                    <td style={{ fontWeight: 500 }}>{job.name}</td>
                    <td>{job.model_type}</td>
                    <td>{job.scenario}</td>
                    <td>
                      <span
                        className={`badge ${
                          job.status === "completed"
                            ? "badge-success"
                            : job.status === "running"
                              ? "badge-info"
                              : job.status === "failed"
                                ? "badge-danger"
                                : job.status === "queued"
                                  ? "badge-warning"
                                  : "badge-neutral"
                        }`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="text-muted">
                      {new Date(job.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
